// src/pages/QualityServices.tsx
import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import {
  FaUserSecret,
  FaClipboardCheck,
  FaChartLine,
  FaShieldAlt,
  FaStar,
  FaCheckCircle,
  FaPaperPlane,
  FaStore,
  FaComments,
  FaFileAlt,
} from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ForfeoPartnerNetworkProgram from '../components/network/ForfeoPartnerNetworkProgram';
import { submitQualityServiceRequest, QualityServiceType } from '../lib/qualityServices';

const serviceCards = [
  {
    type: 'mystery_client' as QualityServiceType,
    icon: FaUserSecret,
    title: 'Client mystère',
    description: 'Une visite réelle, structurée et documentée pour évaluer l’expérience offerte par votre entreprise.',
    bullets: ['Accueil et service', 'Propreté et ambiance', 'Respect de la promesse client', 'Recommandations concrètes'],
  },
  {
    type: 'customer_survey' as QualityServiceType,
    icon: FaClipboardCheck,
    title: 'Sondage client',
    description: 'Un sondage simple et professionnel pour comprendre la satisfaction, les irritants et les opportunités.',
    bullets: ['Questionnaire Forfeo', 'Score de satisfaction', 'Commentaires clients', 'Rapport synthèse'],
  },
  {
    type: 'combined' as QualityServiceType,
    icon: FaChartLine,
    title: 'Forfait qualité complet',
    description: 'Combinez une visite client mystère et un sondage pour obtenir une lecture complète de votre expérience.',
    bullets: ['Vision terrain + feedback client', 'Score qualité Forfeo', 'Plan d’amélioration', 'Rapport premium'],
  },
];

export default function QualityServices() {
  const [selected, setSelected] = useState<QualityServiceType>('combined');
  const [form, setForm] = useState({
    business_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    region: '',
    preferred_period: '',
    visit_goal: '',
    survey_goal: '',
    notes: '',
  });
  const [status, setStatus] = useState<{ type: 'idle' | 'loading' | 'success' | 'error'; message?: string }>({ type: 'idle' });

  const selectedCard = useMemo(() => serviceCards.find((card) => card.type === selected) || serviceCards[2], [selected]);

  const updateField = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus({ type: 'loading', message: 'Transmission de la demande...' });

    try {
      await submitQualityServiceRequest({
        ...form,
        service_type: selected,
        metadata: {
          source: 'public_quality_services_page',
          selected_label: selectedCard.title,
        },
      });

      setStatus({
        type: 'success',
        message: 'Demande envoyée. L’équipe Forfeo vous contactera pour valider les détails.',
      });

      setForm({
        business_name: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        region: '',
        preferred_period: '',
        visit_goal: '',
        survey_goal: '',
        notes: '',
      });
    } catch (error: any) {
      setStatus({
        type: 'error',
        message: error?.message || 'Impossible d’envoyer la demande pour le moment.',
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Helmet>
        <title>Services qualité Forfeo | Clients mystères et sondages</title>
        <meta
          name="description"
          content="Forfeo aide les entreprises locales à mesurer et améliorer leur expérience client grâce aux clients mystères, sondages et rapports qualité."
        />
      </Helmet>

      <Header />

      <main className="overflow-hidden">
        <section className="relative px-4 pb-20 pt-28 sm:px-6 lg:px-8">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.28),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(168,85,247,0.22),transparent_38%)]" />
          <div className="absolute left-1/2 top-16 h-80 w-80 -translate-x-1/2 rounded-full bg-cyan-400/10 blur-3xl" />

          <div className="relative mx-auto max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
              className="max-w-4xl"
            >
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-300/10 px-4 py-2 text-sm font-semibold text-cyan-100">
                <FaShieldAlt />
                Qualité partenaire Forfeo
              </div>

              <h1 className="text-4xl font-black tracking-tight sm:text-6xl">
                Clients mystères, sondages et rapports qualité pour vos expériences.
              </h1>

              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-300">
                Forfeo aide les entreprises à améliorer leur accueil, leur parcours client, leur réputation et leurs ventes grâce à une lecture terrain simple, structurée et actionnable.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#demande"
                  className="rounded-full bg-white px-6 py-3 text-sm font-black text-slate-950 shadow-2xl shadow-cyan-500/20 transition hover:-translate-y-0.5 hover:bg-cyan-50"
                >
                  Demander une évaluation
                </a>
                <a
                  href="#services"
                  className="rounded-full border border-white/15 bg-white/10 px-6 py-3 text-sm font-bold text-white transition hover:bg-white/15"
                >
                  Voir les services
                </a>
              </div>
            </motion.div>

            <div id="services" className="mt-16 grid gap-5 md:grid-cols-3">
              {serviceCards.map((card) => {
                const Icon = card.icon;
                const active = selected === card.type;
                return (
                  <button
                    key={card.type}
                    type="button"
                    onClick={() => setSelected(card.type)}
                    className={`group rounded-[2rem] border p-6 text-left transition ${
                      active
                        ? 'border-cyan-300/70 bg-cyan-300/15 shadow-2xl shadow-cyan-500/10'
                        : 'border-white/10 bg-white/[0.06] hover:border-white/25 hover:bg-white/[0.09]'
                    }`}
                  >
                    <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-slate-950">
                      <Icon className="text-lg" />
                    </div>
                    <h3 className="text-xl font-black">{card.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-slate-300">{card.description}</p>
                    <ul className="mt-5 space-y-2 text-sm text-slate-200">
                      {card.bullets.map((bullet) => (
                        <li key={bullet} className="flex items-center gap-2">
                          <FaCheckCircle className="text-cyan-300" />
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>

            <section className="mt-16 grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="rounded-[2rem] border border-white/10 bg-white/[0.06] p-7">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-300 text-slate-950">
                    <FaStar />
                  </div>
                  <div>
                    <p className="text-sm font-bold uppercase tracking-[0.25em] text-amber-200">Pourquoi Forfeo</p>
                    <h2 className="text-2xl font-black">Un outil de vente, pas seulement un rapport.</h2>
                  </div>
                </div>

                <div className="grid gap-4">
                  {[
                    ['Amélioration', 'Identifiez ce qui bloque la satisfaction ou la conversion.'],
                    ['Réassurance', 'Montrez aux partenaires que Forfeo accompagne la qualité.'],
                    ['Revenus', 'Transformez les recommandations en actions commerciales.'],
                    ['Preuve', 'Chaque rapport est horodaté et documenté.'],
                  ].map(([title, text]) => (
                    <div key={title} className="rounded-2xl border border-white/10 bg-slate-950/40 p-4">
                      <p className="font-black">{title}</p>
                      <p className="mt-1 text-sm leading-6 text-slate-300">{text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <form id="demande" onSubmit={submit} className="rounded-[2rem] border border-white/10 bg-white p-6 text-slate-950 shadow-2xl">
                <div className="mb-6">
                  <p className="text-sm font-black uppercase tracking-[0.25em] text-blue-700">Demande rapide</p>
                  <h2 className="mt-2 text-3xl font-black">Planifier un service qualité</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    Sélection actuelle : <strong>{selectedCard.title}</strong>
                  </p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="block">
                    <span className="text-sm font-bold text-slate-700">Nom de l’entreprise</span>
                    <input value={form.business_name} onChange={(e) => updateField('business_name', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500" required />
                  </label>
                  <label className="block">
                    <span className="text-sm font-bold text-slate-700">Personne contact</span>
                    <input value={form.contact_name} onChange={(e) => updateField('contact_name', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500" />
                  </label>
                  <label className="block">
                    <span className="text-sm font-bold text-slate-700">Courriel</span>
                    <input type="email" value={form.contact_email} onChange={(e) => updateField('contact_email', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500" required />
                  </label>
                  <label className="block">
                    <span className="text-sm font-bold text-slate-700">Téléphone</span>
                    <input value={form.contact_phone} onChange={(e) => updateField('contact_phone', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500" />
                  </label>
                  <label className="block">
                    <span className="text-sm font-bold text-slate-700">Région</span>
                    <input value={form.region} onChange={(e) => updateField('region', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500" placeholder="Québec, Charlevoix..." />
                  </label>
                  <label className="block">
                    <span className="text-sm font-bold text-slate-700">Période souhaitée</span>
                    <input value={form.preferred_period} onChange={(e) => updateField('preferred_period', e.target.value)} className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500" placeholder="Mai, semaine prochaine..." />
                  </label>
                </div>

                <label className="mt-4 block">
                  <span className="text-sm font-bold text-slate-700">Objectif de la visite / du sondage</span>
                  <textarea value={form.visit_goal} onChange={(e) => updateField('visit_goal', e.target.value)} className="mt-2 min-h-[110px] w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500" placeholder="Ex : mesurer l’accueil, tester le parcours réservation, valider l’expérience client..." />
                </label>

                <label className="mt-4 block">
                  <span className="text-sm font-bold text-slate-700">Notes supplémentaires</span>
                  <textarea value={form.notes} onChange={(e) => updateField('notes', e.target.value)} className="mt-2 min-h-[90px] w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500" />
                </label>

                {status.message && (
                  <div className={`mt-4 rounded-2xl p-4 text-sm font-semibold ${
                    status.type === 'success' ? 'bg-emerald-50 text-emerald-800' : status.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-blue-50 text-blue-800'
                  }`}>
                    {status.message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status.type === 'loading'}
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-6 py-4 font-black text-white transition hover:bg-blue-700 disabled:opacity-60"
                >
                  <FaPaperPlane />
                  Envoyer la demande
                </button>
              </form>
            </section>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 py-10">
          <ForfeoPartnerNetworkProgram variant="quality" />
        </section>

      </main>

      <Footer />
    </div>
  );
}
